/**
 * Server error → the sentence a player reads.
 *
 * The server answers every refusal with an envelope naming an i18n key,
 * and that key is the whole message: no status code, no name for the
 * request, no raw body. A response that is not an envelope — the proxy's
 * page while the server restarts, a framework rejection — is described
 * by its status class alone. A raw body is never shown, so nothing
 * internal can reach the screen through one.
 */

/**
 * Backend error wire shape — the JSON envelope `{ error, i18n_key, args }`.
 * The i18n_key resolves
 * through the active i18n bundle so a future locale switch updates
 * the message without a server round trip.
 *
 * Wire shape:
 *   - `error` — English short-form for debugging / log search
 *     (server-side logs already grep on this); never user-facing.
 *   - `i18n_key` — bundle key (e.g. `"auth.login_failed"`) the
 *     frontend resolves through the active i18n bundle.
 *   - `args` — optional ICU interpolation params (e.g. `{ count: 3 }`).
 *
 * When the envelope is absent the message is told by the status class
 * (`formatServerError`). The `params` alias is still accepted.
 */
export interface BackendErrorEnvelope {
  /** English short-form debugging surface. Server logs grep this;
   *  never rendered to the user. Optional on the TS side so a
   *  pre-rename server (or a partial envelope) parses cleanly. */
  readonly error?: string;
  readonly i18n_key: string;
  /** ICU MessageFormat interpolation arguments. Maps to the
   *  `{var}` placeholders inside the bundle entry. */
  readonly args?: Readonly<Record<string, string | number>>;
}

/**
 * Parse the response body as a backend i18n envelope, or `null`. Accepts
 * both `args` and the older `params` naming.
 */
export function tryParseBackendError(
  body: string,
): BackendErrorEnvelope | null {
  if (body === null || body === undefined || body.length === 0) return null;
  const trimmed = body.trim();
  if (!trimmed.startsWith("{")) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const obj = parsed as Record<string, unknown>;
  if (typeof obj.i18n_key !== "string") return null;
  const rawArgs = obj.args ?? obj.params;
  const out: {
    -readonly [K in keyof BackendErrorEnvelope]: BackendErrorEnvelope[K];
  } = {
    i18n_key: obj.i18n_key,
  };
  if (typeof obj.error === "string") {
    out.error = obj.error;
  }
  if (
    typeof rawArgs === "object" &&
    rawArgs !== null &&
    !Array.isArray(rawArgs)
  ) {
    out.args = rawArgs as Readonly<Record<string, string | number>>;
  }
  return out;
}

/**
 * Resolve a backend i18n envelope through the active i18n bundle.
 * The `i18next` singleton is loaded lazily via the
 * `globalThis.__arkheI18n` hook (set in `main.tsx`) so this
 * helper stays independent of the i18n init chain — pre-init
 * callers fall back to the `error` debug field (or the raw key
 * when both are absent) so the user never sees a blank string.
 */
export function resolveBackendError(envelope: BackendErrorEnvelope): string {
  const i18nModule = (
    globalThis as unknown as {
      __arkheI18n?: { t: (k: string, p?: Record<string, unknown>) => string };
    }
  ).__arkheI18n;
  if (i18nModule === undefined) {
    return envelope.error ?? envelope.i18n_key;
  }
  return i18nModule.t(envelope.i18n_key, envelope.args);
}

/** Resolve a bundle key through the lazily-bound i18n singleton.
 *  Falls back to the supplied default so this helper stays usable
 *  before `main.tsx` wires `__arkheI18n` on cold boot. */
function tBundle(
  key: string,
  fallback: string,
  params?: Record<string, unknown>,
): string {
  const i18nModule = (
    globalThis as unknown as {
      __arkheI18n?: { t: (k: string, p?: Record<string, unknown>) => string };
    }
  ).__arkheI18n;
  if (i18nModule === undefined) return fallback;
  return i18nModule.t(key, params);
}

/**
 * The message for a refused request.
 *
 * `traceId`, when the server stamped one, is appended for support.
 */
export function formatServerError(
  status: number,
  body: string,
  traceId?: string | null,
): string {
  const envelope = tryParseBackendError(body ?? "");
  const message =
    envelope !== null ? resolveBackendError(envelope) : statusMessage(status);
  if (traceId !== undefined && traceId !== null && traceId.length > 0) {
    return tBundle("error.trace_id_suffix", `${message} (추적: ${traceId})`, {
      message,
      traceId,
    });
  }
  return message;
}

/** A response with no envelope, told by its status class. */
function statusMessage(status: number): string {
  if (status === 401) {
    return tBundle("error.unauthorized", "다시 로그인해 주세요.");
  }
  if (status === 403) {
    return tBundle("error.forbidden", "이 요청은 처리할 수 없어요.");
  }
  if (status === 404) {
    return tBundle("error.not_found", "찾을 수 없어요.");
  }
  if (status === 429) {
    return tBundle(
      "error.too_many_requests",
      "요청이 너무 많아요. 잠시 후 다시 시도해 주세요.",
    );
  }
  if (status >= 500) {
    return tBundle(
      "error.server_busy",
      "서버에 잠시 연결할 수 없어요. 잠시 후 다시 시도해 주세요.",
    );
  }
  return tBundle(
    "error.request_failed",
    "요청을 처리하지 못했어요. 다시 시도해 주세요.",
  );
}

/**
 * Wire minor 28 — map a `HandError.reason` invariant token into a
 * Korean toast string. The BE emits stable tokens (e.g.
 * `"settle_invariant"`, `"chip_conservation"`) so the FE renders a
 * targeted message instead of the generic "{label} 실패" banner.
 *
 * Unknown tokens fall through to a generic sentence; the token itself
 * is the server log's business, not the player's. The i18n bundle keys
 * follow `error.hand.<token>` so future locales swap copy without
 * a code change.
 */
export function mapServerErrorToToast(reason: string): string {
  const token = (reason ?? "").trim().toLowerCase();
  switch (token) {
    case "settle_invariant":
    case "chip_conservation":
      return tBundle(
        "error.hand.settle_invariant",
        "이번 핸드 정산이 검증에 실패해서 서버가 핸드를 중단했어요. 다음 핸드에서 자동 복구돼요.",
      );
    case "stack_underflow":
      return tBundle(
        "error.hand.stack_underflow",
        "스택 차감 정합성이 깨졌어요. 다음 핸드에서 자동 복구돼요.",
      );
    case "all_in_math":
      return tBundle(
        "error.hand.all_in_math",
        "올인 정산 계산이 맞지 않아 핸드가 중단됐어요. 다음 핸드에서 자동 복구돼요.",
      );
    default:
      return tBundle(
        "error.hand.generic",
        "핸드가 중단됐어요. 다음 핸드에서 자동 복구돼요.",
      );
  }
}
